import { engagementTermsAPI, type CampaignEngagementTerms, type EngagementCreator } from "@/api/engagementTerms";

export interface EngagementTermsCache {
	marketTerms: Map<string, CampaignEngagementTerms | null>;
	marketTermsRequests: Map<string, Promise<CampaignEngagementTerms | null>>;
	marketCreators: Map<string, EngagementCreator[]>;
	marketCreatorsRequests: Map<string, Promise<EngagementCreator[]>>;
	creatorTerms: Map<string, CampaignEngagementTerms | null>;
	creatorTermsRequests: Map<string, Promise<CampaignEngagementTerms | null>>;
	creatorVersions: Map<string, CampaignEngagementTerms[]>;
	creatorVersionsRequests: Map<string, Promise<CampaignEngagementTerms[]>>;
}

export const createEngagementTermsCache = (): EngagementTermsCache => ({
	marketTerms: new Map(),
	marketTermsRequests: new Map(),
	marketCreators: new Map(),
	marketCreatorsRequests: new Map(),
	creatorTerms: new Map(),
	creatorTermsRequests: new Map(),
	creatorVersions: new Map(),
	creatorVersionsRequests: new Map(),
});

export const clearEngagementTermsCache = (cache: EngagementTermsCache) => {
	cache.marketTerms.clear();
	cache.marketTermsRequests.clear();
	cache.marketCreators.clear();
	cache.marketCreatorsRequests.clear();
	cache.creatorTerms.clear();
	cache.creatorTermsRequests.clear();
	cache.creatorVersions.clear();
	cache.creatorVersionsRequests.clear();
};

export const getEngagementTermsMarketKey = (campaignId: string, marketBlockId: string) =>
	`${campaignId}:${marketBlockId}`;

export const getEngagementTermsCreatorKey = (campaignId: string, marketBlockId: string, creatorAssignmentId: string) =>
	`${getEngagementTermsMarketKey(campaignId, marketBlockId)}:${creatorAssignmentId}`;

const loadCached = <T>(
	key: string,
	values: Map<string, T>,
	requests: Map<string, Promise<T>>,
	load: () => Promise<T>,
): Promise<T> => {
	if (values.has(key)) return Promise.resolve(values.get(key) as T);
	const activeRequest = requests.get(key);
	if (activeRequest) return activeRequest;

	const request: Promise<T> = load()
		.then((value) => {
			if (requests.get(key) === request) values.set(key, value);
			return value;
		})
		.finally(() => {
			if (requests.get(key) === request) requests.delete(key);
		});
	requests.set(key, request);
	return request;
};

export const loadCachedMarketTerms = (cache: EngagementTermsCache, campaignId: string, marketBlockId: string) =>
	loadCached(
		getEngagementTermsMarketKey(campaignId, marketBlockId),
		cache.marketTerms,
		cache.marketTermsRequests,
		() => engagementTermsAPI.getMarketTerms(campaignId, marketBlockId),
	);

export const loadCachedMarketCreators = (cache: EngagementTermsCache, campaignId: string, marketBlockId: string) =>
	loadCached(
		getEngagementTermsMarketKey(campaignId, marketBlockId),
		cache.marketCreators,
		cache.marketCreatorsRequests,
		() => engagementTermsAPI.getMarketCreators(campaignId, marketBlockId),
	);

export const loadCachedCreatorTerms = (
	cache: EngagementTermsCache,
	campaignId: string,
	marketBlockId: string,
	creatorAssignmentId: string,
) =>
	loadCached(
		getEngagementTermsCreatorKey(campaignId, marketBlockId, creatorAssignmentId),
		cache.creatorTerms,
		cache.creatorTermsRequests,
		() => engagementTermsAPI.getCreatorTerms(campaignId, marketBlockId, creatorAssignmentId),
	);
export const loadCachedCreatorVersions = (
	cache: EngagementTermsCache,
	campaignId: string,
	marketBlockId: string,
	creatorAssignmentId: string,
) =>
	loadCached(
		getEngagementTermsCreatorKey(campaignId, marketBlockId, creatorAssignmentId),
		cache.creatorVersions,
		cache.creatorVersionsRequests,
		() => engagementTermsAPI.getCreatorTermVersions(campaignId, marketBlockId, creatorAssignmentId),
	);
export const preloadEngagementTermsMarket = (cache: EngagementTermsCache, campaignId: string, marketBlockId: string) =>
	Promise.allSettled([
		loadCachedMarketTerms(cache, campaignId, marketBlockId),
		loadCachedMarketCreators(cache, campaignId, marketBlockId),
	]);

export const invalidateCachedCreatorTermsForMarket = (
	cache: EngagementTermsCache,
	campaignId: string,
	marketBlockId: string,
) => {
	const prefix = `${getEngagementTermsMarketKey(campaignId, marketBlockId)}:`;
	for (const key of cache.creatorTerms.keys()) {
		if (key.startsWith(prefix)) cache.creatorTerms.delete(key);
	}
	for (const key of cache.creatorTermsRequests.keys()) {
		if (key.startsWith(prefix)) cache.creatorTermsRequests.delete(key);
	}
	for (const key of cache.creatorVersions.keys()) {
		if (key.startsWith(prefix)) cache.creatorVersions.delete(key);
	}
	for (const key of cache.creatorVersionsRequests.keys()) {
		if (key.startsWith(prefix)) cache.creatorVersionsRequests.delete(key);
	}
};
